import { TrendingDown, TrendingUp } from "lucide-react";
import React from "react";
import { useFormatting } from "../../hooks/useFormatting";
import { useTerminology } from "../../hooks/useTerminology";
import { formatDate } from "../../lib/utils";
import type {
  ScenarioStatus,
  ScenarioVersion,
  ScenarioVersionAdjustment,
} from "../../types/scenarios";
import { formatStatus } from "../forecast/ScenarioOverlayPicker";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { categoryLabels, type ScenarioCategory } from "./scenarioCategory";

interface VersionViewerModalProps {
  version: ScenarioVersion;
  open: boolean;
  onClose: () => void;
}

export const VersionViewerModal: React.FC<VersionViewerModalProps> = ({
  version,
  open,
  onClose,
}) => {
  const { formatCurrency } = useFormatting();
  const { getScenarioTerm } = useTerminology();
  const versionData = version.data;
  const adjustments = versionData.adjustments || [];

  // Format an adjustment type string into a human-readable label
  const formatAdjustmentType = (type: string): string => {
    return type
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Render the detail line for an adjustment: net cash impact, then the adjusted
  // value or percentage, falling back to the adjustment type.
  const renderAdjustmentDetails = (adj: ScenarioVersionAdjustment) => {
    const hasNetImpact = typeof adj.netCashImpact === "number";
    const hasValue = typeof adj.adjustedValue === "number";
    const hasPercentage = typeof adj.adjustmentValuePercentage === "number";

    return (
      <div className="text-muted-foreground">
        {hasNetImpact && (
          <div>
            Net impact: {formatCurrency(adj.netCashImpact as number, "full")}
          </div>
        )}
        {hasValue && (
          <div>{formatCurrency(adj.adjustedValue as number, "full")}</div>
        )}
        {hasPercentage && (
          <div>
            {(adj.adjustmentValuePercentage as number) > 0 ? "+" : ""}
            {adj.adjustmentValuePercentage}%
          </div>
        )}
        {!hasNetImpact && !hasValue && !hasPercentage && (
          <div>{formatAdjustmentType(adj.adjustmentType)}</div>
        )}
      </div>
    );
  };

  // Group adjustments by direction — inflows (revenue) vs outflows (cost)
  const revenueAdjustments = adjustments.filter(
    (a) => a.adjustmentDirection === "inflow",
  );
  const costAdjustments = adjustments.filter(
    (a) => a.adjustmentDirection === "outflow",
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div>
            <DialogTitle className="text-2xl">
              Version {version.version} (Historical)
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Created by {version.createdByName} on{" "}
              {formatDate(version.createdAt)}
            </p>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Adjustments Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Adjustments in This Version
              </CardTitle>
            </CardHeader>
            <CardContent>
              {adjustments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No adjustments in this version
                </p>
              ) : (
                <div className="space-y-4">
                  {/* Inflow (revenue) Adjustments */}
                  {revenueAdjustments.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        <h4 className="font-medium">
                          Revenue ({revenueAdjustments.length})
                        </h4>
                      </div>
                      <div className="space-y-2 ml-6">
                        {revenueAdjustments.map((adj, idx) => (
                          <div
                            key={adj.id ?? idx}
                            className="text-sm border-l-2 border-green-200 pl-3 py-1"
                          >
                            <div className="font-medium">
                              {adj.description ||
                                formatAdjustmentType(adj.adjustmentType)}
                            </div>
                            {renderAdjustmentDetails(adj)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Outflow (cost) Adjustments */}
                  {costAdjustments.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingDown className="h-4 w-4 text-blue-600" />
                        <h4 className="font-medium">
                          Cost ({costAdjustments.length})
                        </h4>
                      </div>
                      <div className="space-y-2 ml-6">
                        {costAdjustments.map((adj, idx) => (
                          <div
                            key={adj.id ?? idx}
                            className="text-sm border-l-2 border-blue-200 pl-3 py-1"
                          >
                            <div className="font-medium">
                              {adj.description ||
                                formatAdjustmentType(adj.adjustmentType)}
                            </div>
                            {renderAdjustmentDetails(adj)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Scenario Metadata */}
          {versionData.name && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {getScenarioTerm(true)} Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <span className="ml-2 font-medium">{versionData.name}</span>
                  </div>
                  {versionData.category && (
                    <div>
                      <span className="text-muted-foreground">Category:</span>
                      <Badge className="ml-2" variant="outline">
                        {categoryLabels[
                          versionData.category as ScenarioCategory
                        ] ?? versionData.category}
                      </Badge>
                    </div>
                  )}
                  {versionData.status && (
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <Badge className="ml-2" variant="outline">
                        {formatStatus(versionData.status as ScenarioStatus)}
                      </Badge>
                    </div>
                  )}
                  {versionData.createdByName && (
                    <div>
                      <span className="text-muted-foreground">Owner:</span>
                      <span className="ml-2 font-medium">
                        {versionData.createdByName}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Read-Only Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This is a historical version and cannot be
              edited. To make changes based on this version, use the "Restore"
              button to create a new version.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
